"use client";

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";

type Theme = "system" | "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** The resolved mode actually applied (never "system"). */
  resolved: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => {},
  resolved: "light",
});

const STORAGE_KEY = "menzil-theme";
const FORCED_EVENT = "menzil:forced-theme";

function getSystemPreference(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * A surface that owns the color scheme: the Telegram Mini App follows the
 * Telegram client's light or dark theme, not the site preference. Its
 * layout marks <html data-forced-theme> before the first paint; while the
 * mark is there the stored choice and the OS setting are ignored, and
 * nothing is written to the stored choice.
 */
function forcedTheme(): "light" | "dark" | null {
  if (typeof document === "undefined") return null;
  const v = document.documentElement.dataset.forcedTheme;
  return v === "light" || v === "dark" ? v : null;
}

export function setForcedTheme(mode: "light" | "dark" | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode) root.dataset.forcedTheme = mode;
  else delete root.dataset.forcedTheme;
  window.dispatchEvent(new Event(FORCED_EVENT));
}

function applyClass(resolved: "light" | "dark", animate = false) {
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-transition");
  }
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // Keep the inline style in sync with the active theme. The root
  // layout's blocking init script sets these same inline properties
  // pre-paint to prevent the first-frame flash. If we only toggled
  // `.dark` here, switching from dark → light at runtime would leave
  // the inline `backgroundColor: dark` painted on <html>, masking
  // whatever bg CSS wants to apply. So we update inline styles too.
  if (resolved === "dark") {
    root.style.colorScheme = "dark";
    root.style.backgroundColor = "oklch(0.130 0.012 286)";
  } else {
    root.style.colorScheme = "light";
    root.style.backgroundColor = "oklch(0.985 0 0)";
  }
  if (animate) {
    // Remove the transition class after the animation completes so
    // normal interactions (hover, focus) aren't slowed down.
    setTimeout(() => root.classList.remove("theme-transition"), 350);
  }
}

/** The stored choice (or the system's) applied to <html>, for a page whose
 *  head script never ran: the error page after a failed full load, where
 *  React draws the root layout itself and does not run its inline
 *  scripts, so dark-mode readers got the light page. Harmless where the
 *  script did run: the same mode is applied again. */
export function applyStoredTheme(): void {
  if (typeof document === "undefined") return;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const theme: Theme = stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
    applyClass(forcedTheme() ?? (theme === "system" ? getSystemPreference() : theme));
  } catch {
    // Storage blocked: the page keeps the look it has.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("system");
  const [resolved, setResolved] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  // On mount, read saved preference
  useEffect(() => {
    let saved: Theme | null = null;
    try { saved = localStorage.getItem(STORAGE_KEY) as Theme | null; } catch { /* storage off */ }
    const t = saved && ["system", "light", "dark"].includes(saved) ? saved : "system";
    setThemeState(t);
    const r = forcedTheme() ?? (t === "system" ? getSystemPreference() : t);
    setResolved(r);
    applyClass(r);
    setMounted(true);
  }, []);

  // Listen for system preference changes when in "system" mode, and for
  // a surface taking over or handing back the scheme.
  useEffect(() => {
    if (!mounted) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const forced = forcedTheme();
      if (forced || theme === "system") {
        const r = forced ?? getSystemPreference();
        setResolved(r);
        applyClass(r);
      } else {
        setResolved(theme);
        applyClass(theme);
      }
    };
    mq.addEventListener("change", handler);
    window.addEventListener(FORCED_EVENT, handler);
    return () => {
      mq.removeEventListener("change", handler);
      window.removeEventListener(FORCED_EVENT, handler);
    };
  }, [theme, mounted]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
    const r = t === "system" ? getSystemPreference() : t;
    setResolved(r);
    applyClass(r, true); // animate user-initiated toggles
  }, []);

  const value = useMemo(() => ({ theme, setTheme, resolved }), [theme, setTheme, resolved]);
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

